import * as jsonpatch from "fast-json-patch";
import CommonUtil from "./CommonUtil";

class PatchUtils {
  static _NEW_ARRAY_ELEMENT_ID = "-";
  static _OPERATION_TREE_PROPERTY = "_operation";

  /**
   * Compares new and old entity. Returns json-patch operations for patch request.
   * It trims values so that unimportant pieces are not sent and only
   * values specified in oldEntity (so called observed values) are tracked.
   */
  static compare = (
    oldEntity,
    entity,
    validPaths = undefined,
    invalidPaths = undefined,
    alwaysIncludedPaths = [],
    resetArrayPaths = undefined
  ) => {
    const resetArrayOps = PatchUtils._getResetArrayOps(
      oldEntity,
      resetArrayPaths
    );

    const oldForCompare = PatchUtils._getOldEntityForCompare(
      oldEntity,
      alwaysIncludedPaths,
      resetArrayPaths
    );

    const ops = jsonpatch.compare(oldForCompare, entity);

    const rectifiedOps = PatchUtils._rectifyReplaceWithNewOperations(ops);
    const trimmedOps = PatchUtils._trimValues(
      rectifiedOps,
      oldEntity,
      validPaths,
      invalidPaths,
      alwaysIncludedPaths
    );
    const groupedOps = CommonUtil.groupArrayByProperty(trimmedOps, "path");
    var transformedOps = PatchUtils._transformGroupedOperations(groupedOps);

    transformedOps = resetArrayOps.concat(transformedOps);

    return transformedOps;
  };

  static _getResetArrayOps = (oldEntity, resetArrayPaths) => {
    var resetArrayOps = [];

    if (resetArrayPaths) {
      for (let i = 0; i < resetArrayPaths.length; i++) {
        const array = CommonUtil.getDeep(oldEntity, resetArrayPaths[i]);
        if (array) {
          const arrayOps = jsonpatch.compare(array, []);
          for (let j = 0; j < arrayOps.length; j++) {
            arrayOps[j].path = `${resetArrayPaths[i]}${arrayOps[j].path}`;
          }
          resetArrayOps = resetArrayOps.concat(arrayOps);
        }
      }
    }

    return resetArrayOps;
  };

  static _getOldEntityForCompare = (
    oldEntity,
    alwaysIncludedPaths,
    resetArrayPaths
  ) => {
    var oldForCompare = oldEntity;

    if (alwaysIncludedPaths || resetArrayPaths) {
      oldForCompare = CommonUtil.jsonCopy(oldEntity);

      if (alwaysIncludedPaths) {
        CommonUtil.deleteDeepObjectProperties(
          oldForCompare,
          alwaysIncludedPaths
        );
      }
      if (resetArrayPaths) {
        for (let i = 0; i < resetArrayPaths.length; i++) {
          CommonUtil.setDeepOnStringPath(oldForCompare, resetArrayPaths[i], []);
        }
      }
    }

    return oldForCompare;
  };

  static _rectifyReplaceWithNewOperations = operations => {
    const opsAsTree = CommonUtil.groupArrayToTreeByProperty(
      operations,
      "path",
      "/",
      PatchUtils._OPERATION_TREE_PROPERTY
    );

    CommonUtil.processTree(opsAsTree, (root, childName) => {
      const child = root[childName];

      if (child) {
        const childOp = child[PatchUtils._OPERATION_TREE_PROPERTY];
        const isLeaf = CommonUtil.objectHasOnlyOneProperty(
          child,
          PatchUtils._OPERATION_TREE_PROPERTY
        );

        if (
          isLeaf &&
          childName === "id" &&
          (childOp.op === "remove" || childOp.op === "replace")
        ) {
          const siblingReplaceOpNames = CommonUtil.filterTreeNodeSiblingNamesWithDeepValue(
            root,
            childName,
            `${PatchUtils._OPERATION_TREE_PROPERTY}.op`,
            "replace"
          );

          if (siblingReplaceOpNames && siblingReplaceOpNames.length > 0) {
            CommonUtil.deleteObjectProperties(
              root,
              siblingReplaceOpNames.concat("id")
            );
            root.id[PatchUtils._OPERATION_TREE_PROPERTY].op = "replace";
          }
        }
      }
    });

    const opsAsArray = CommonUtil.ungroupTreeToArrayByProperty(
      opsAsTree,
      PatchUtils._OPERATION_TREE_PROPERTY
    );
    return opsAsArray;
  };

  static _trimValues = (
    operations,
    oldEntity,
    validPaths,
    invalidPaths,
    alwaysIncludedPaths
  ) => {
    return operations
      .filter(op =>
        PatchUtils._validateOperation(
          op,
          oldEntity,
          validPaths,
          invalidPaths,
          alwaysIncludedPaths
        )
      )
      .map(op =>
        PatchUtils._transformOperation(
          op,
          oldEntity,
          validPaths,
          alwaysIncludedPaths
        )
      );
  };

  static _transformGroupedOperations = ops => {
    var operations = [];

    Object.keys(ops).forEach(path => {
      const pathOps = ops[path];

      if (path.endsWith(PatchUtils._NEW_ARRAY_ELEMENT_ID)) {
        operations = operations.concat(pathOps);
      } else if (
        CommonUtil.arrayHasObjectWithProperty(pathOps, "op", "remove")
      ) {
        operations.push(PatchUtils._getPathOperation("remove", pathOps, true));

        if (pathOps && pathOps.length > 0) {
          operations.push(
            PatchUtils._transformPathOperationsToDifferentOperation(
              path,
              pathOps,
              "add"
            )
          );
        }
      } else {
        operations.push(
          PatchUtils._constructOperationWithJointValue(path, pathOps)
        );
      }
    });

    return operations;
  };

  static _transformPathOperationsToDifferentOperation = (
    path,
    pathOps,
    newOp
  ) => {
    const op = PatchUtils._constructOperationWithJointValue(path, pathOps);

    op.op = newOp;
    if (newOp === "add") {
      op.path = CommonUtil.replaceStringAfterLastOccurrence(
        op.path,
        "/",
        PatchUtils._NEW_ARRAY_ELEMENT_ID
      );
    }

    return op;
  };

  static _getPathOperation = (opName, pathOps, shouldRemove = false) => {
    const lastIndex = CommonUtil.lastIndexOfObjectWithPoperty(
      pathOps,
      "op",
      opName
    );
    const pathOperation = pathOps[lastIndex];

    if (shouldRemove) {
      CommonUtil.removeFromArrayAtIndex(pathOps, lastIndex);
    }

    return pathOperation;
  };

  static _constructOperationWithJointValue = (path, pathOps) => {
    const op = pathOps[0].op;
    const value =
      pathOps.length > 1
        ? CommonUtil.accumulateArrayProperties(pathOps, "value")
        : pathOps[0].value;

    return {
      op: op,
      path: path,
      value: value
    };
  };

  static _validateOperation = (
    op,
    oldEntity,
    validPaths,
    invalidPaths,
    alwaysIncludedPaths
  ) => {
    const pathTree = op.path.split("/");
    const lastPath = pathTree[pathTree.length - 1];
    const isFirstLevelProperty = pathTree.length <= 2;
    const validEnd =
      lastPath === "id" ||
      lastPath === "dateFrom" ||
      lastPath === "dateTo" ||
      !isNaN(lastPath);
    const hasKey = Object.keys(oldEntity).some(key => pathTree[1] === key);
    const isContainedInValidPaths = PatchUtils._isValidPath(
      op.path,
      validPaths
    );
    const isValidReplaceOperation = op.op === "replace" && op.value;
    const isInvalidPath = PatchUtils._isValidPath(op.path, invalidPaths);
    const isAlwaysIncludedPath = PatchUtils._isValidPath(
      op.path,
      alwaysIncludedPaths
    );

    return (
      hasKey &&
      !isInvalidPath &&
      (isFirstLevelProperty ||
        validEnd ||
        isContainedInValidPaths ||
        isValidReplaceOperation ||
        isAlwaysIncludedPath)
    );
  };

  static _isValidPath = (path, validPaths) => {
    if (validPaths) {
      for (var i = 0; i < validPaths.length; i++) {
        if (new RegExp(validPaths[i]).test(path)) {
          return true;
        }
      }
    }
    return false;
  };

  static _transformOperation = (
    op,
    oldEntity,
    validPaths,
    alwaysIncludedPaths
  ) => {
    if (op.value && typeof op.value === "object") {
      op.value = PatchUtils._constructValue(op, ["/id"], alwaysIncludedPaths);
    } else if (
      op.path.endsWith("id") ||
      PatchUtils._isValidPath(op.path, validPaths) ||
      PatchUtils._isValidPath(op.path, alwaysIncludedPaths)
    ) {
      const pathTree = op.path.split("/");
      const prop = pathTree.pop();
      op.path = pathTree.join("/");
      const value = {};
      value[prop] = op.value;
      op.value = value;
    }

    let arrayId = CommonUtil.substringAfterLastOccurrence(op.path, "/");
    if (!isNaN(arrayId)) {
      arrayId = parseInt(arrayId);
      const pathTree = op.path.split("/");
      const oldValue = oldEntity[pathTree[1]];
      if (
        !oldValue ||
        (Array.isArray(oldValue) &&
          (oldValue.length <= arrayId || op.op === "add"))
      ) {
        op.path = CommonUtil.replaceStringAfterLastOccurrence(
          op.path,
          "/",
          PatchUtils._NEW_ARRAY_ELEMENT_ID
        );
      }
    }
    return op;
  };

  static _constructValue = (op, identifierPaths, alwaysIncludedPaths) => {
    const value = {};

    if (alwaysIncludedPaths) {
      alwaysIncludedPaths = identifierPaths.concat(alwaysIncludedPaths);
      const paths = CommonUtil.getAllObjectPropertiesPaths(op.value, "/", true);
      var pathsToInclude = paths.filter(path => {
        for (let i = 0; i < alwaysIncludedPaths.length; i++) {
          if (
            new RegExp(alwaysIncludedPaths[i]).test(`${op.path}${path.path}`)
          ) {
            return true;
          }
        }
        return false;
      });

      if (
        op.op === "add" &&
        (pathsToInclude === undefined || pathsToInclude.length === 0) &&
        identifierPaths !== undefined &&
        identifierPaths.length > 0
      ) {
        pathsToInclude = paths.filter(path => {
          for (let i = 0; i < identifierPaths.length; i++) {
            if (new RegExp(identifierPaths[i]).test(`${op.path}${path.path}`)) {
              return false;
            }
          }
          return path.path !== "/";
        });
      }

      pathsToInclude.forEach(path => {
        CommonUtil.setDeepOnStringPath(value, path.path, path.val, true);
      });
    }

    return value;
  };
}

export default PatchUtils;
